import React, { useState } from "react";
import {
  Breadcrumb,
  Upload,
  Button,
  message,
  Card,
  Typography,
  Space,
  Popconfirm,
} from "antd";
import {
  UploadOutlined,
  FileExcelOutlined,
  BuildingOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import Excel from "exceljs";
import FlexCard from "@components/flex-card";
import AuthenticatedLayout from "../../layouts/authenticated";
import { useTranslation } from "react-i18next";
import {
  validarLinha,
  validarColunas,
  retornarLinhas,
} from "../../util/validacaoExcel";
import axios from "axios";
import LogsCard from "../../components/logs-card";
import PlanilhaEmpresas from "../../assets/planilhas/layout_empresas.xlsx";
import PlanilhaUnidades from "../../assets/planilhas/layout_unidades.xlsx";
import { ambienteNomeSelector } from "../../store/autenticacao";
import { useSelector } from "react-redux";
import BreadcrumbTitulo from "@/components/breadcrumb-titulo";

const { Text } = Typography;

function ImportadorArquivosPage() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [sucessos, setSucessos] = useState([]);
  const [tipoAba, setTipoAba] = useState("");
  const ambienteNome = useSelector(ambienteNomeSelector);

  const handleBeforeUpload = (file) => {
    const isXlsx =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");
    if (!isXlsx) {
      message.error("Apenas arquivos .xlsx são permitidos");
      return Upload.LIST_IGNORE;
    }
    setFile(file);
    setLogs([]);
    setSucessos([]);
    setTipoAba("");
    setTimeout(() => handleProcessar(file), 0);
    return false;
  };

  const handleProcessar = async (selectedFile = file) => {
    if (!selectedFile) {
      message.warning("Selecione um arquivo primeiro!");
      return;
    }
    setProcessing(true);
    setLogs([
      {
        type: "info",
        message: "Iniciando processamento...",
        numero_linha: null,
      },
    ]);
    setSucessos([]);
    setTipoAba("");

    try {
      const workbook = new Excel.Workbook();
      const arrayBuffer = await selectedFile.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];
      setTipoAba(worksheet.name);

      const errosColunas = validarColunas(worksheet);
      if (errosColunas.length > 0) {
        setLogs(
          errosColunas.map((msg) => ({
            type: "error",
            message: msg,
            numero_linha: null,
          }))
        );
        message.error("Arquivo inválido: colunas obrigatórias ausentes.");
        setProcessing(false);
        return;
      }

      const novosLogs = [];
      let totalLinhas = 0;
      let totalErros = 0;
      const linhasSucesso = [];
      const validarLinhas = [];

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 2) {
          const sucessosLinha = retornarLinhas(
            row,
            rowNumber,
            worksheet.name
          );
          if (sucessosLinha.length > 0)
            validarLinhas.push(...sucessosLinha);
        }
      });

      const arrayErrosRequest = await handlevalidar(
        worksheet.name,
        validarLinhas
      );

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 2) {
          const erroBack = arrayErrosRequest?.find(
            (e) => e.numero_linha === rowNumber
          );
          if (erroBack) {
            novosLogs.push({
              type: "error",
              message: erroBack.erro,
              numero_linha: rowNumber,
            });
            totalErros++;
          } else {
            const { erros, sucessos: sucessosLinha } = validarLinha(
              row,
              rowNumber,
              worksheet.name
            );
            erros.forEach((msg) =>
              novosLogs.push({
                type: "error",
                message: msg,
                numero_linha: rowNumber,
              })
            );
            totalErros += erros.length;
            if (sucessosLinha.length > 0)
              linhasSucesso.push(...sucessosLinha);
          }
          totalLinhas++;
        }
      });

      setSucessos(linhasSucesso);
      novosLogs.unshift({
        type: "success",
        message: `Processamento concluído! ${totalLinhas} linhas verificadas, ${totalErros} erro(s) encontrado(s).`,
        numero_linha: null,
      });
      setLogs(novosLogs);
    } catch {
      setLogs([
        {
          type: "error",
          message: "Erro ao processar o arquivo.",
          numero_linha: null,
        },
      ]);
      message.error("Erro ao processar o arquivo.");
    } finally {
      setProcessing(false);
    }
  };

  const handleCriar = async () => {
    if (!tipoAba) return;
    try {
      await axios.post(
        tipoAba === "Empresas"
          ? "/empresas/lista"
          : "/empresas/unidades/lista",
        sucessos
      );
      message.success(`${tipoConfig.text} criadas com sucesso!`);
      setFile(null);
      setLogs([]);
      setSucessos([]);
      setTipoAba("");
    } catch {
      message.error("Erro ao criar itens");
    }
  };

  const handlevalidar = async (aba, validarLinhas) => {
    if (!aba) return [];
    try {
      const response = await axios.post(
        aba === "Empresas"
          ? "/empresas/validar"
          : "/empresas/unidades/validar",
        validarLinhas
      );
      return response.data;
    } catch {
      message.error("Erro ao validar itens");
      return [];
    }
  };

  const tipoConfig = (() => {
    if (tipoAba === "Empresas")
      return { icon: BuildingOutlined, color: "#1890ff", text: "Empresas" };
    if (tipoAba === "Unidades")
      return { icon: ShopOutlined, color: "#52c41a", text: "Unidades" };
    return { icon: null, color: "#000", text: "" };
  })();

  return (
    <AuthenticatedLayout breadcrumb={[t("Importador de arquivos")]}>
      <FlexCard title={<BreadcrumbTitulo selected={t("Importador de arquivos")} />} headStyle={{ backgroundColor: "#f0f0f06b" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
          {!file && (
            <>
              <Text type="secondary">
                Selecione um arquivo .xlsx para validar os dados.
              </Text>
              <Card size="small" title="1. Seleção do Arquivo">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Upload.Dragger beforeUpload={handleBeforeUpload} maxCount={1} showUploadList={false} style={{ padding: 20 }}>
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined
                        style={{ fontSize: 32, color: "#1890ff" }}
                      />
                    </p>
                    <p className="ant-upload-text">
                      Clique ou arraste um arquivo para esta área
                    </p>
                    <p className="ant-upload-hint">
                      Apenas arquivos .xlsx são permitidos.
                    </p>
                  </Upload.Dragger>
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                      marginTop: 12,
                    }}
                  >
                    <Button
                      icon={<FileExcelOutlined />}
                      href={PlanilhaEmpresas}
                      download="layout_empresas.xlsx"
                    >
                      Baixar Layout Empresas
                    </Button>
                    <Button icon={<FileExcelOutlined />} href={PlanilhaUnidades} download="layout_unidades.xlsx">
                      Baixar Layout Unidades
                    </Button>
                  </div>
                </Space>
              </Card>
            </>
          )}
          {file && (
            <div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div>
                  <Text strong>Arquivo selecionado:</Text> {file.name}
                </div>
             <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
  {logs.some((log) => log.type === "error") ||
  sucessos.length === 0 ||
  !tipoAba ? (
    <Button
      type="primary"
      disabled
      style={{
        padding: "6px 24px",
        fontWeight: 500,
        minWidth: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        lineHeight: 1,
      }}
    >
      Criar {tipoConfig.text + "(s)"}
    </Button>
  ) : (
    <Popconfirm
      title={`Tem certeza que deseja criar ${tipoConfig.text}(s)?`}
      onConfirm={handleCriar}
      okText="Confirmar"
      cancelText="Cancelar"
    >
      <Button
        type="primary"
        style={{
          padding: "6px 24px",
          fontWeight: 500,
          minWidth: 180,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          lineHeight: 1,
        }}
      >
        Criar {tipoConfig.text + "(s)"}
      </Button>
    </Popconfirm>
  )}
  <Button
    onClick={() => {
      const header = [
        "============================================================",
        "                 LOGS DE PRÉ-PROCESSAMENTO                  ",
        "============================================================",
        `Plataforma: LegalPanel`,
        `Ambiente: ${ambienteNome}`,
        `Arquivo: ${file?.name || "N/A"}`,
        `Data: ${new Date().toLocaleString("pt-BR")}`,
        "============================================================",
        "",
      ];

      const linhas = logs.map((log) => {
        const tipoTraduzido =
          log.type === "error"
            ? "ERRO"
            : log.type === "success"
            ? "Leitura"
            : log.type.toUpperCase();

        return `[${tipoTraduzido}] ${
          log.numero_linha ? `Linha ${log.numero_linha}: ` : ""
        }${log.message}`;
      });

      const conteudoFinal = [...header, ...linhas].join("\n");

      const blob = new Blob([conteudoFinal], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "logs_preprocessamento.txt";
      a.click();
      window.URL.revokeObjectURL(url);
    }}
  >
    Baixar Logs Pré-processamento
  </Button>
  <Button
    danger
    onClick={() => {
      setFile(null);
      setLogs([]);
      setSucessos([]);
      setTipoAba("");
    }}
  >
    Limpar tudo
  </Button>
</div>

              </div>
              <LogsCard title="Resumo" logs={logs} />
            </div>
          )}
        </div>
      </FlexCard>
    </AuthenticatedLayout>
  );
}

export default ImportadorArquivosPage;
